import React, { useState, useEffect } from 'react';
import './Gastos.css';
import Header from '../common/Header';
import Footer from '../common/Footer';
import FormModal from '../common/FormModal';
import ConfirmModal from '../common/ConfirmModal';
import FormField from '../common/FormField';
import FormGrid from '../common/FormGrid';
import { useToast } from '../../contexts/ToastContext';
import { getApiUrl } from '../../utils/api';
import Select from 'react-select';
import { FaSearch, FaSave, FaPlus, FaEdit, FaTrash, FaMoneyBillWave, FaCalendarAlt, FaTag } from 'react-icons/fa';

// Obtener fecha actual en horario de México (America/Mexico_City)
const getFechaMexico = () => {
  const now = new Date();
  const mexicoDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Mexico_City' }));
  const year = mexicoDate.getFullYear();
  const month = String(mexicoDate.getMonth() + 1).padStart(2, '0');
  const day = String(mexicoDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getHoraMexico = () => {
  return new Date().toLocaleString('es-MX', {
    timeZone: 'America/Mexico_City',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

const Gastos = () => {
  const [gastos, setGastos] = useState([]);
  const [tiposGasto, setTiposGasto] = useState([]);
  const [cortes, setCortes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [gastoSeleccionado, setGastoSeleccionado] = useState(null);
  const [gastoAEliminar, setGastoAEliminar] = useState(null);
  const [totalGastos, setTotalGastos] = useState(0);

  // Campos del formulario
  const [formData, setFormData] = useState({
    concepto: '',
    monto: '',
    idTipoGasto: '',
    fecha: getFechaMexico()
  });

  // Filtros
  const [fechaInicio, setFechaInicio] = useState(getFechaMexico());
  const [fechaFin, setFechaFin] = useState(getFechaMexico());
  const [corteSeleccionado, setCorteSeleccionado] = useState('');

  const toast = useToast();

  useEffect(() => {
    cargarTiposGasto();
    cargarCortes();
  }, []);

  useEffect(() => {
    if (cortes.length > 0) {
      cargarGastos();
    }
  }, [fechaInicio, fechaFin, corteSeleccionado, cortes]);

  useEffect(() => {
    if (cortes.length > 0 && !corteSeleccionado) {
      // Seleccionar automáticamente el primer corte (actual)
      setCorteSeleccionado(cortes[0].idCorte);
    }
  }, [cortes]);

  const cargarTiposGasto = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'getTiposGasto'
        })
      });

      const data = await response.json();

      if (data.success) {
        setTiposGasto(data.data || []);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar tipos de gasto:', error);
      toast.error('Error al cargar tipos de gasto');
    }
  };

  const cargarCortes = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'getCortes'
        })
      });

      const data = await response.json();

      if (data.success) {
        setCortes(data.data || []);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar cortes:', error);
      toast.error('Error al cargar cortes');
    }
  };

  const cargarGastos = async () => {
    try {
      setLoading(true);

      // Determinar fechas basadas en el corte seleccionado
      let fechaInicioCorte = fechaInicio;
      let fechaFinCorte = fechaFin;
      // Para "hoy" se filtra además por hora: desde el fin del corte hasta el momento actual
      let rangoHoy = null;

      if (corteSeleccionado && cortes.length > 0) {
        const corte = cortes.find(c => c.idCorte === corteSeleccionado);
        if (corte) {
          if (corte.idCorte === 'hoy') {
            const fechaActual = getFechaMexico();
            const fechaFinDelCorte = corte.fechaISO || fechaActual;
            const horaFinDelCorte = corte.hora || '00:00:00';

            fechaInicioCorte = fechaFinDelCorte;
            fechaFinCorte = fechaActual;
            rangoHoy = {
              inicio: `${fechaFinDelCorte} ${horaFinDelCorte}`,
              fin: `${fechaActual} ${getHoraMexico()}`
            };
          } else if (corte.idCorte === 'actual') {
            fechaInicioCorte = corte.fechaISO || fechaInicio;
            fechaFinCorte = corte.fechaISO || fechaFin;
          } else {
            fechaInicioCorte = corte.fechaAnteriorISO || corte.fechaISO || fechaInicio;
            fechaFinCorte = corte.fechaISO || fechaFin;
          }
        }
      }

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'getGastos',
          fechaInicio: fechaInicioCorte,
          fechaFin: fechaFinCorte
        })
      });

      const data = await response.json();

      if (data.success) {
        let gastosData = data.data || [];

        if (rangoHoy) {
          gastosData = gastosData.filter(gasto => {
            const marcaGasto = `${gasto.fecha} ${gasto.hora || '00:00:00'}`;
            return marcaGasto >= rangoHoy.inicio && marcaGasto <= rangoHoy.fin;
          });
        }

        setGastos(gastosData);
        calcularTotal(gastosData);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar gastos:', error);
      toast.error('Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  };

  const calcularTotal = (gastosData) => {
    const total = gastosData.reduce((sum, gasto) => sum + parseFloat(gasto.monto || 0), 0);
    setTotalGastos(total);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const nuevoGasto = () => {
    setFormData({
      concepto: '',
      monto: '',
      idTipoGasto: '',
      fecha: getFechaMexico()
    });
    setGastoSeleccionado(null);
    setShowModal(true);
  };

  const editarGasto = (gasto) => {
    setFormData({
      concepto: gasto.concepto || '',
      monto: gasto.monto || '',
      idTipoGasto: gasto.idTipoGasto || '',
      fecha: gasto.fecha || getFechaMexico()
    });
    setGastoSeleccionado(gasto);
    setShowModal(true);
  };

  const guardarGasto = async () => {
    try {
      setGuardando(true);

      if (!formData.concepto.trim()) {
        toast.error('El concepto es obligatorio');
        return;
      }

      if (!formData.monto || parseFloat(formData.monto) <= 0) {
        toast.error('El monto debe ser mayor a 0');
        return;
      }

      if (!formData.idTipoGasto) {
        toast.error('Debe seleccionar un tipo de gasto');
        return;
      }

      // Solo permitir fecha actual de México al crear nuevo gasto
      if (!gastoSeleccionado && formData.fecha !== getFechaMexico()) {
        toast.error('Solo se permite registrar gastos con la fecha actual');
        return;
      }

      const endpoint = gastoSeleccionado ? 'actualizarGasto' : 'crearGasto';
      const payload = {
        tipo: endpoint,
        ...formData,
        fecha: getFechaMexico(),
        hora: getHoraMexico(),
        usuario: 'Usuario Actual' // Aquí puedes usar el usuario actual del contexto
      };

      if (gastoSeleccionado) {
        payload.idGasto = gastoSeleccionado.idGasto;
      }

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        toast.success(gastoSeleccionado ? 'Gasto actualizado correctamente' : 'Gasto creado correctamente');
        handleCloseModal();
        cargarGastos();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al guardar gasto:', error);
      toast.error('Error al guardar gasto');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminarGasto = (gasto) => {
    setGastoAEliminar(gasto);
    setShowDeleteModal(true);
  };

  const eliminarGasto = async () => {
    if (!gastoAEliminar) return;

    try {
      setGuardando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'eliminarGasto',
          idGasto: gastoAEliminar.idGasto
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Gasto eliminado correctamente');
        setShowDeleteModal(false);
        setGastoAEliminar(null);
        cargarGastos();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al eliminar gasto:', error);
      toast.error('Error al eliminar gasto');
    } finally {
      setGuardando(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setGastoSeleccionado(null);
    setFormData({
      concepto: '',
      monto: '',
      idTipoGasto: '',
      fecha: getFechaMexico()
    });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setGastoAEliminar(null);
  };

  if (loading && gastos.length === 0) {
    return (
      <div className="gastos-container">
        <div className="loading">Cargando gastos...</div>
      </div>
    );
  }

  return (
    <div className="gastos-container">
      <Header
        titulo='Gastos'
        color='#667eea'
      />
      <div className="gastos-content">
        <div className="gastos-inner">
          <div className="gastos-header">
            <h1 className="gastos-titulo">Registro de Gastos</h1>
            <div className="botones-header">
              <button
                onClick={nuevoGasto}
                className="btn-nuevo"
              >
                <FaPlus /> Nuevo
              </button>
            </div>
          </div>

          <div className="panel-filtros">
            <div className="filtros-content">
              <div className="filtros-row-horizontal">
                <div className="filtro-item">
                  <label>Fecha:</label>
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className="input-filtro"
                  />
                </div>

                <div className="filtro-item">
                  <label>Cortes:</label>
                  <select
                    className="select-input"
                    value={corteSeleccionado}
                    onChange={(e) => setCorteSeleccionado(e.target.value)}
                  >
                    {cortes.map(corte => (
                      <option key={corte.idCorte} value={corte.idCorte}>
                        {corte.idCorte === 'actual' ? 'Actual' :
                         corte.idCorte === 'hoy' ? 'Hoy' :
                         `${corte.fecha} ${corte.hora}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="btn-mostrar-gastos">
                  <button
                    className="btn-buscar"
                    onClick={cargarGastos}
                  >
                    ✓ Mostrar Gastos (F1)
                  </button>
                </div>

                <div className="total-gastos-box">
                  <div className="total-label">Total de Gastos</div>
                  <div className="total-amount">$ {totalGastos.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>


          <div className="panel-listado">
            <div className="tabla-container">
              {loading ? (
                <div className="cargando">Cargando gastos...</div>
              ) : gastos.length === 0 ? (
                <div className="sin-datos">No se encontraron gastos en el período seleccionado.</div>
              ) : (
                <table className="tabla-gastos">
                  <thead>
                    <tr>
                      <th>Concepto</th>
                      <th>Monto</th>
                      <th>Usuario</th>
                      <th>Descripción</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gastos.map(gasto => (
                      <tr key={gasto.idGasto}>
                        <td className="concepto-gasto">{gasto.concepto}</td>
                        <td className="monto-gasto">$ {parseFloat(gasto.monto || 0).toFixed(2)}</td>
                        <td>{gasto.usuario}</td>
                        <td>{gasto.tipoGastoDescripcion}</td>
                        <td>
                          <div className="acciones-gasto">
                            <button
                              onClick={() => editarGasto(gasto)}
                              className="btn-accion btn-editar"
                              title="Editar gasto"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => confirmarEliminarGasto(gasto)}
                              className="btn-accion btn-eliminar"
                              title="Eliminar gasto"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="acciones-container">
              <div className="info-gastos">
                <span>Total de gastos: {gastos.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Modal para crear/editar gasto */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={gastoSeleccionado ? 'Editar Gasto' : 'Nuevo Gasto'}
        onSave={guardarGasto}
        saveLabel={gastoSeleccionado ? 'Actualizar' : 'Crear'}
        isSaving={guardando}
        size="medium"
        saveIcon={<FaMoneyBillWave />}
        saveButtonType="button"
      >
        <FormField
          label="Concepto"
          name="concepto"
          value={formData.concepto}
          onChange={handleInputChange}
          required
          placeholder="Descripción del gasto"
          icon={<FaTag />}
        />

        <FormGrid columns={2} gap="16px">
          <FormField
            label="Monto"
            type="number"
            name="monto"
            value={formData.monto}
            onChange={handleInputChange}
            required
            placeholder="0.00"
            step="0.01"
            min="0"
            icon={<FaMoneyBillWave />}
          />

          <FormField
            label="Fecha"
            type="date"
            name="fecha"
            value={formData.fecha}
            onChange={handleInputChange}
            icon={<FaCalendarAlt />}
            disabled={!gastoSeleccionado}
            min={!gastoSeleccionado ? getFechaMexico() : undefined}
            max={!gastoSeleccionado ? getFechaMexico() : undefined}
          />
        </FormGrid>

        <div className="form-field">
          <label className="form-field-label">Tipo de Gasto <span style={{ color: '#ef4444' }}>*</span></label>
          <Select
            value={tiposGasto
              .map(tipo => ({ value: tipo.idTipoGasto, label: tipo.descripcion }))
              .find(opt => opt.value === formData.idTipoGasto) || null}
            onChange={(selectedOption) =>
              setFormData(prev => ({ ...prev, idTipoGasto: selectedOption ? selectedOption.value : '' }))
            }
            options={tiposGasto.map(tipo => ({
              value: tipo.idTipoGasto,
              label: tipo.descripcion
            }))}
            placeholder="Buscar tipo de gasto..."
            isSearchable={true}
            isClearable={true}
            noOptionsMessage={() => "No se encontraron tipos de gasto"}
            className="select-tipo-gasto"
            classNamePrefix="select"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
      </FormModal>

      {/* Modal de confirmación para eliminar */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={eliminarGasto}
        title="Eliminar Gasto"
        message={`¿Está seguro de eliminar el gasto "${gastoAEliminar?.concepto}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        type="danger"
        isLoading={guardando}
      />
    </div>
  );
};

export default Gastos;